import { z } from 'zod';
import { isoDateTime, jsonValue, riskLevel, uuid } from './primitives';
import type { ConnectionProvider } from './connections';
import { toolDefinition, toolCallIntent } from './tool';
import { canonicalStep } from './steps';
import { wakeEvent } from './events';
import { normalizedError } from './errors';

/**
 * The AgentRuntime port's data contracts (§8.2). `EpisodeInput.executeTool` is a
 * runtime function binding (§8.4), NOT part of the serializable contract — the
 * schema below covers only the data the platform assembles from canonical state.
 */

/** Advisory per-episode limits; the platform enforces authoritatively (§7.4). */
export const episodeLimits = z.object({
  maxTurnsPerEpisode: z.number().int().positive(),
  maxToolCallsPerRun: z.number().int().positive(),
  maxCostUsdPerRun: z.number().positive(),
  maxWallclockMinutes: z.number().positive(),
});
export type EpisodeLimits = z.infer<typeof episodeLimits>;

/**
 * Agent kind (§13.1, v1.2). `inbox` is the v1 triage/draft agent; `research` is the
 * read-only multi-session research agent. Default `inbox` — every existing
 * agent/version is `inbox` and behaves byte-for-byte as before. A `research` agent
 * additionally carries a `research` config block and its `toolGrants` are read-only
 * by construction (enforced at publish, defence-in-depth over I2/D7/D15).
 */
export const agentKind = z.enum(['inbox', 'research', 'task']);
export type AgentKind = z.infer<typeof agentKind>;

/**
 * How a version's core task prompt is sourced (§13.2, #147). ORTHOGONAL to `kind`:
 *   - `static`    — the prompt is fixed in the immutable version and every run uses
 *                   it verbatim. The default, and the only mode compatible with a
 *                   `schedule` trigger (a scheduled run has no chance to edit a prompt).
 *   - `templated` — the operator edits the core task prompt per run. The per-run text
 *                   lands on the RUN (`agent_runs.task_prompt`), NOT a new version —
 *                   I10 still holds: a run references exactly one immutable version.
 *                   Manual-only: a `templated` version may not carry a `schedule`
 *                   trigger (enforced cross-field where `triggers` are visible).
 * Approval (D15/I2/I3) still gates every write regardless of the per-run prompt.
 * Default `static` — an existing version with no `runMode` parses/behaves as before.
 */
export const runMode = z.enum(['static', 'templated']);
export type RunMode = z.infer<typeof runMode>;

/** The default run mode when a version omits one (§13.2, #147). */
export const DEFAULT_RUN_MODE: RunMode = 'static';

/**
 * Autonomy posture for a `task` agent (§13.3, v1.6) — the owner's one coarse control
 * over what runs UN-approved. It SUPERSEDES per-tool autonomy for task agents and is
 * enforced at the gateway (reads / `sideEffect:'none'` always run free in every
 * posture):
 *   - `approval_only` (default) — every mutation requires approval.
 *   - `non_send`               — non-send mutations run un-approved; SENDS require approval.
 *   - `full`                   — everything runs un-approved. The SOLE exception to D15
 *                                (all-sends-require-approval): opt-in, owner-only,
 *                                fully audited, behind a stern publish-time double-confirm.
 * Meaningful only for `kind:'task'`; a non-default posture on any other kind is rejected
 * at publish. The kill-switch (§7.7) halts every posture.
 */
export const autonomyPosture = z.enum(['approval_only', 'non_send', 'full']);
export type AutonomyPosture = z.infer<typeof autonomyPosture>;

/** The default autonomy posture when a version omits one (§13.3) — safest; the owner
 * must consciously loosen it. Equivalent to "no posture" for non-task kinds. */
export const DEFAULT_AUTONOMY_POSTURE: AutonomyPosture = 'approval_only';

/**
 * The Claude model a version's episodes run on (#89). A plain string id — NO vendor
 * type crosses the port (I4); the adapter passes it straight into the SDK `query()`
 * `model` option. The enum is the allow-list the platform offers in the builder;
 * `DEFAULT_MODEL` (Sonnet 5) is applied when a version omits `model`, so every
 * existing version stays backward-compatible and picks the cheaper balanced model
 * rather than the old hardcoded Opus default. Ordered cheapest → most capable.
 */
export const modelId = z.enum([
  'claude-haiku-4-5-20251001', // Haiku 4.5 — cheapest, fastest
  'claude-sonnet-5', // Sonnet 5 — balanced default
  'claude-opus-4-8', // Opus 4.8 — most capable
]);
export type ModelId = z.infer<typeof modelId>;

/** The default model when a version omits one (§6.2, #89). Cheaper than Opus. */
export const DEFAULT_MODEL: ModelId = 'claude-sonnet-5';

/** Short human labels + one-line cost/capability hints for the builder UI (#89). */
export const MODEL_OPTIONS: ReadonlyArray<{ id: ModelId; label: string; hint: string }> = [
  {
    id: 'claude-haiku-4-5-20251001',
    label: 'Haiku 4.5',
    hint: 'Cheapest and fastest — best for simple triage and high volume.',
  },
  {
    id: 'claude-sonnet-5',
    label: 'Sonnet 5',
    hint: 'Balanced cost and capability — the recommended default.',
  },
  {
    id: 'claude-opus-4-8',
    label: 'Opus 4.8',
    hint: 'Most capable and most expensive — for the hardest work.',
  },
];

/**
 * A research target (§13.1). An EXTENSIBLE discriminated union on `kind`: WP-R6 adds
 * `{ kind:'hubspot' }` here with NO refactor of consumers.
 *   - `web`     — hosted WebSearch (implies `researchMode:'hosted'`). Optional
 *                 `keySites` are user-suggested domains to prioritise (WebSearch
 *                 `allowed_domains` where the SDK supports it; WP-R3). WebFetch stays off.
 *   - `email`   — read-only search over the connected mailbox across inbox/sent/drafts
 *                 (`email.search`, `scope:'all_mail'`; never `email.send`).
 *   - `calendar` — read-only view of the connected Google Calendar (`calendar.list`,
 *                 `calendar.find_availability`; never any calendar mutation). Shares the
 *                 SAME google connection as the `email` target (§11.2).
 *   - `hubspot` — read-only search/read over the connected HubSpot CRM
 *                 (contacts/companies/deals/notes/emails/tasks) via `crm.search`/
 *                 `crm.read`, plus relationship traversal via `crm.associations`
 *                 (WP-R6, #125). Read-only by construction (no CRM write tools exist),
 *                 routed through the gateway with connection resolution (#67/#69).
 */
export const researchTarget = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('web'),
    keySites: z.array(z.string().min(1)).optional(),
  }),
  z.object({
    kind: z.literal('email'),
    scope: z.literal('all_mail'),
  }),
  z.object({
    kind: z.literal('calendar'),
  }),
  z.object({
    kind: z.literal('hubspot'),
  }),
  // §11.5 (v1.7): search/read the connected Google account's Drive. Requires a `google`
  // connection (same one that backs email/calendar); no extra options in v1.
  z.object({
    kind: z.literal('drive'),
  }),
]);
export type ResearchTarget = z.infer<typeof researchTarget>;

/** A research run's authoritative time (minutes) or cost (USD) limit (§13.1, §7.4). */
export const researchLimit = z.object({
  kind: z.enum(['time', 'cost']),
  value: z.number().positive(),
});
export type ResearchLimit = z.infer<typeof researchLimit>;

/**
 * The `research` config block on a `kind:'research'` version (§13.1). Immutable, part
 * of the published version. Absent for `inbox` agents. `researchMode` on the parent
 * config still governs hosted WebSearch enablement (§13); a `web` target implies
 * `researchMode:'hosted'`.
 */
export const researchConfig = z.object({
  prompt: z.string().min(1),
  targets: z.array(researchTarget).min(1),
  limit: researchLimit,
});
export type ResearchConfig = z.infer<typeof researchConfig>;

/**
 * The immutable agent-version config the platform passes into an episode (§6.2).
 *
 * `kind` (default `inbox`) and the optional `research` block are the v1.2 additions
 * (§13.1). The two are correlated by a cross-field refinement: a `research` block is
 * present iff `kind==='research'`. When a `research` config carries a `web` target,
 * `researchMode` must be `hosted` (a research agent that searches the web is a hosted
 * agent, §13). Inbox agents omit both and validate/behave exactly as before.
 */
export const agentVersionConfigBase = z.object({
  // Optional on the base episode contract so runtime consumers that assemble a
  // config need not restate the default; the publish `versionConfig` schemas below
  // normalize it to `inbox` at parse. Treat an absent `kind` as `inbox` (§13.1).
  kind: agentKind.optional(),
  // How this version's core task prompt is sourced (§13.2, #147). ORTHOGONAL to
  // `kind`. Optional on the base contract — like `kind`/`model` — so runtime
  // consumers that assemble a config need not restate the default; the publish
  // `versionConfig` schemas below normalize it to `static` at parse. Treat an absent
  // `runMode` as `static` (§13.2) — an existing version is unchanged.
  runMode: runMode.optional(),
  // Autonomy posture for a `task` agent (§13.3, v1.6) — the owner's coarse control over
  // what runs un-approved. Optional on the base contract (like `kind`/`runMode`/`model`)
  // so runtime consumers need not restate the default; the publish `versionConfig`
  // normalizes it to `approval_only` at parse. Only meaningful for `kind:'task'`; a
  // non-default posture on another kind is rejected at publish (refine below).
  autonomyPosture: autonomyPosture.optional(),
  // The model this version's episodes run on (#89). Optional on the base contract so
  // runtime consumers need not restate the default; the publish `versionConfig`
  // schemas below normalize it to `DEFAULT_MODEL` (Sonnet 5) at parse. Treat an
  // absent `model` as the default — existing versions run on the cheaper model.
  model: modelId.optional(),
  instructions: z.string(),
  goals: z.string(),
  completionCriteria: z.string(),
  toolGrants: z.array(
    z.object({ tool: z.string(), autonomy: z.enum(['allowed', 'requires_approval']) }),
  ),
  researchMode: z.enum(['hosted', 'disabled']),
  limits: episodeLimits,
  research: researchConfig.optional(),
  /**
   * Per-agent connection grants (#70/#107): the explicit set of connection ids this
   * version may operate against. OPTIONAL and defence-in-depth — when ABSENT the
   * loader falls back to the v1 workspace projection (#69) so existing versions keep
   * working unchanged. When PRESENT, publish validates each id (exists + active +
   * in-workspace, I5) and provider COVERAGE (an inbox agent's email/calendar tools
   * require a 'google' connection; a research 'email' target requires 'google', a
   * 'hubspot' target requires 'hubspot'), and the gateway resolves a connection-bound
   * tool to the GRANTED connection whose provider matches the tool's connector
   * provider (email/calendar→google, crm→hubspot). A SET (not one) because a research
   * agent may need a Google mailbox AND a HubSpot connection at once.
   */
  connections: z.array(uuid).optional(),
  /**
   * Whether this agent may process IMAGE attachments as model vision (#145). OPT-IN
   * per agent, DEFAULT off: image vision spends image/vision tokens (cost), so an
   * agent reads image attachments only when this is explicitly enabled. When absent
   * or false, `email.get_attachment` returns image attachments as metadata + a short
   * note (no bytes, no vision block); text/PDF/Word/PowerPoint extraction is
   * UNAFFECTED — always on regardless of this flag. Optional on the base contract so
   * runtime consumers need not restate the default; treat absent as false.
   */
  processImageAttachments: z.boolean().optional(),
});
export type AgentVersionConfigBase = z.infer<typeof agentVersionConfigBase>;

/**
 * The `kind`/`research` cross-field rules (§13.1), factored out so both the shared
 * `versionConfig` (agents.ts) and the server's `.extend`ed `versionConfig` can apply
 * them after adding `triggers`. `.superRefine` on the extended object keeps the
 * publish payload validated end-to-end without duplicating the base shape.
 */
export function refineAgentVersionConfig(
  cfg: AgentVersionConfigBase,
  ctx: z.RefinementCtx,
): void {
  if (cfg.kind === 'research' && !cfg.research) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['research'],
      message: "A research agent requires a 'research' config block.",
    });
  }
  if (cfg.kind !== 'research' && cfg.research) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['research'],
      message: "A 'research' config block is only valid for kind:'research'.",
    });
  }
  if (cfg.research?.targets.some((t) => t.kind === 'web') && cfg.researchMode !== 'hosted') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['researchMode'],
      message: "A research agent with a 'web' target requires researchMode:'hosted'.",
    });
  }
  // §13.3: a loosened autonomy posture (`non_send`/`full`) is only meaningful for a
  // `task` agent — the posture governs which of a task agent's ACTIONS run un-approved.
  // Reject it on any other kind (fail closed) so an owner never believes they enabled
  // autonomy on an inbox/research agent. `approval_only` (or absent) is the safe
  // no-op default and is valid on every kind.
  if (cfg.kind !== 'task' && cfg.autonomyPosture && cfg.autonomyPosture !== 'approval_only') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['autonomyPosture'],
      message: "autonomyPosture other than 'approval_only' is only valid for kind:'task'.",
    });
  }
}

/**
 * The `runMode`/`triggers` cross-field rule (§13.2, #147). Factored out separately
 * from `refineAgentVersionConfig` because it needs the `triggers` list, which lives
 * on the EXTENDED `versionConfig` (agents.ts / server schemas), not on the base. A
 * `templated` version is manual-only: a scheduled run has no chance to edit the
 * per-run prompt, so a `schedule` trigger requires a fixed (`static`) prompt.
 * Applied by the extended `versionConfig` `.superRefine`s alongside
 * `refineAgentVersionConfig`. The base `agentVersionConfig` carries no triggers, so
 * this constraint does not apply there.
 */
export function refineRunModeTriggers(
  cfg: AgentVersionConfigBase & { triggers?: Array<{ kind: string }> },
  ctx: z.RefinementCtx,
): void {
  const hasSchedule = (cfg.triggers ?? []).some((t) => t.kind === 'schedule');
  if (hasSchedule && cfg.runMode === 'templated') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['runMode'],
      message:
        'templated agents run manually only — a schedule requires a fixed (static) prompt.',
    });
  }
}

export const agentVersionConfig = agentVersionConfigBase.superRefine(refineAgentVersionConfig);
export type AgentVersionConfig = z.infer<typeof agentVersionConfig>;

/**
 * Provider a connection-bound tool routes to, derived from its canonical name
 * prefix (#107). This is the SINGLE mapping publish-coverage validation and the
 * gateway's provider-matched resolution agree on: `email.*`/`calendar.*` → the
 * Google connector (`provider: 'google'`), `crm.*` → HubSpot. A tool that needs no
 * connection (`fake_email`, canary — no provider prefix here) returns `null`. The
 * gateway confirms the real provider from `registered.connector.provider`; this
 * name-prefix map lets publish validation reason about coverage WITHOUT the runtime
 * registry.
 */
export function providerForConnectionBoundTool(tool: string): ConnectionProvider | null {
  // §11.5 (v1.7): `drive.*` routes to the SAME google connection as email/calendar.
  if (tool.startsWith('email.') || tool.startsWith('calendar.') || tool.startsWith('drive.'))
    return 'google';
  if (tool.startsWith('crm.')) return 'hubspot';
  // §11.3 (v1.9): `dispatch.*` (the newsletter MCP connector) routes to the managed
  // `dispatch` connection.
  if (tool.startsWith('dispatch.')) return 'dispatch';
  // §11.3 (v1.9.1): `pitchreply.*` (the second MCP connector) routes to the `pitchreply`
  // connection.
  if (tool.startsWith('pitchreply.')) return 'pitchreply';
  return null;
}

/**
 * The set of connection providers a published version MUST be granted to be
 * coverable (#107). Union of: every connection-bound tool grant's provider
 * (`email`/`calendar`→google, `crm`→hubspot) AND — for a research version — each
 * research target's provider (`email`→google, `calendar`→google, `hubspot`→hubspot;
 * `web` needs none — email and calendar share one google connection, §11.2).
 * Empty when the version needs no connection (an inbox agent granting only
 * connection-less tools, a web-only research agent). Publish validation rejects a
 * version whose grant list does not cover this set.
 */
export function requiredProvidersForConfig(config: AgentVersionConfigBase): ConnectionProvider[] {
  const providers = new Set<ConnectionProvider>();
  for (const grant of config.toolGrants) {
    const provider = providerForConnectionBoundTool(grant.tool);
    if (provider) providers.add(provider);
  }
  for (const target of config.research?.targets ?? []) {
    // §11.5: `drive` (like email/calendar) resolves to the one google connection (§11.2).
    if (target.kind === 'email' || target.kind === 'calendar' || target.kind === 'drive')
      providers.add('google');
    else if (target.kind === 'hubspot') providers.add('hubspot');
    // `web` targets use hosted WebSearch — no connection required. `email` and
    // `calendar` both resolve to the one google connection (§11.2).
  }
  return [...providers];
}

/** Bounded snapshot of canonical run state handed to the adapter (§7.5). */
export const runSnapshot = z.object({
  runId: uuid,
  workspaceId: uuid,
  goal: z.string(),
  progressSummary: z.string(),
  recentSteps: z.array(canonicalStep),
  pendingWakes: z.array(wakeEvent),
  remaining: z.object({
    turns: z.number().int().nonnegative(),
    toolCalls: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
  }),
});
export type RunSnapshot = z.infer<typeof runSnapshot>;

/** Normalized usage reported by an episode (§8.2). */
export const normalizedUsage = z.object({
  // Authoritative cost, COMPUTED from the model pricing table (#195) — not the
  // SDK's opaque `total_cost_usd` estimate (which rides on `sdkEstimatedCostUsd`).
  costUsd: z.number().nonnegative(),
  // TOTAL input tokens = uncached delta + cache-creation + cache-read (#195). With
  // prompt caching the bulk lives in the cache tiers, so summing all three is the
  // real input count (the SDK's `input_tokens` alone is only the uncached delta).
  tokensIn: z.number().int().nonnegative(),
  tokensOut: z.number().int().nonnegative(),
  modelIds: z.array(z.string()),
  // Optional auditable breakdown (#195) — back-compat: existing consumers ignore
  // these. `tokensIn` already folds the cache tiers in; these expose the split.
  cacheCreationTokens: z.number().int().nonnegative().optional(),
  cacheReadTokens: z.number().int().nonnegative().optional(),
  // The SDK's own opaque cost estimate, kept for cross-check/observability only.
  sdkEstimatedCostUsd: z.number().nonnegative().optional(),
});
export type NormalizedUsage = z.infer<typeof normalizedUsage>;

/**
 * A hosted-research citation (§6.5 `artifacts.sources`, §13). One retrieved web
 * source the final output cites: the URL, its title, and when it was accessed.
 */
export const artifactSource = z.object({
  url: z.string().url(),
  title: z.string(),
  accessedAt: isoDateTime,
});
export type ArtifactSource = z.infer<typeof artifactSource>;

/**
 * One hosted WebSearch the episode performed (§13). The hook mirror records the
 * query, coarse results metadata (count + result URLs), and timing — mirrored
 * into `agent_run_steps` + a `usage_records` row `kind='search'` and counted
 * toward the run's tool-call budget. Search-only is the sole I2 exemption (D7):
 * the query string is model output, so it is fully audited (residual exfil risk,
 * §13/§16.4). WebFetch is never exposed, in any mode.
 */
export const researchSearch = z.object({
  query: z.string(),
  /** Coarse results metadata mirrored for the transcript (no page bodies — search only). */
  resultCount: z.number().int().nonnegative().optional(),
  resultUrls: z.array(z.string()).optional(),
  /** Milliseconds the search took (provider-side), when the runtime reports it. */
  durationMs: z.number().nonnegative().optional(),
});
export type ResearchSearch = z.infer<typeof researchSearch>;

/**
 * The `body` of an artifact (§6.5): ONE concrete shape, no dual-typed column.
 * `format` picks which optional field carries the content.
 */
export const artifactBody = z.object({
  format: z.enum(['markdown', 'json', 'email', 'binary_ref']),
  text: z.string().optional(),
  data: jsonValue.optional(),
  ref: z.string().optional(),
});
export type ArtifactBody = z.infer<typeof artifactBody>;

/**
 * An artifact an episode produced (§6.5 `artifacts`): a draft email, report, file,
 * or structured output. Outputs that cite the web carry `sources` (hosted-research
 * citations), surfaced in the run view (§13). Authored by the episode; the platform
 * stamps id/workspace/run and persists it.
 */
export const artifactDraft = z.object({
  kind: z.enum(['draft_email', 'report', 'file', 'structured']),
  title: z.string(),
  body: artifactBody,
  sources: z.array(artifactSource).nullable().optional(),
});
export type ArtifactDraft = z.infer<typeof artifactDraft>;

/** Serializable portion of EpisodeInput (executeTool binding excluded — §8.4). */
export const episodeInputData = z.object({
  run: runSnapshot,
  agentVersion: agentVersionConfig,
  wake: wakeEvent,
  tools: z.array(toolDefinition),
  limits: episodeLimits,
  runtimeSessionRef: z.string().optional(),
});
export type EpisodeInputData = z.infer<typeof episodeInputData>;

/**
 * EpisodeResult (§8.2): common fields + a discriminated terminal `status`.
 * `steps` are model turns + intents only (no tool executions — §7.2).
 */
const episodeResultBase = z.object({
  usage: normalizedUsage,
  steps: z.array(canonicalStep),
  progressSummary: z.string(),
  runtimeSessionRef: z.string().optional(),
  /**
   * Hosted searches the episode performed (§13, researchMode='hosted' only). The
   * runner mirrors each into a `system` step + a `usage_records` row `kind='search'`
   * and counts them toward the tool-call budget. Empty/absent when research is off.
   */
  searches: z.array(researchSearch).optional(),
  /**
   * True iff `searches` were ALREADY streamed to the runner as `system` transcript
   * steps DURING the episode (#190, mirroring #171's streamed `steps`) — the runner
   * must NOT also batch-write a `system` step for each search at persist time (that
   * would double it), though it must still write the `usage_records` row for each
   * (that ledger write only ever happens once, at persist time, in both paths).
   * Absent/false ⇒ the runner batches the mirror `system` step itself (the pre-#190
   * behaviour, and the path a scripted/mock adapter that only returns `searches`
   * takes — it never streams).
   */
  searchesStreamed: z.boolean().optional(),
  /**
   * Artifacts the episode produced (§6.5). Outputs citing the web carry `sources`.
   * The runner persists these to the `artifacts` table, surfaced in the run view.
   */
  artifacts: z.array(artifactDraft).optional(),
});

export const episodeResult = z.discriminatedUnion('status', [
  episodeResultBase.extend({ status: z.literal('completed'), summary: z.string() }),
  episodeResultBase.extend({
    status: z.literal('waiting'),
    resumeAt: isoDateTime.optional(),
    resumeOnEvent: z.string().optional(),
    reason: z.string(),
  }),
  episodeResultBase.extend({
    status: z.literal('needs_approval'),
    proposedCall: toolCallIntent,
    argsHash: z.string(),
    reason: z.string(),
    risk: riskLevel.optional(),
  }),
  episodeResultBase.extend({
    status: z.literal('blocked'),
    reason: z.string(),
    requiredInput: z.string().optional(),
  }),
  episodeResultBase.extend({
    status: z.literal('limit_reached'),
    limit: z.enum(['turns', 'cost', 'wallclock']),
  }),
  /**
   * A NATURAL END (#131): the model finished its turn and stopped WITHOUT calling a
   * control tool, and it was NOT a genuine per-episode maxTurns exhaustion (that
   * surfaces as `limit_reached:'turns'`). The model's final assistant text rides as
   * `summary`. The runner maps this to COMPLETED for an inbox run (the agent decided
   * no further action was needed) and to the #98 continue/finalise path for research —
   * so a "no action needed" inbox run is COMPLETED, never a spurious LIMIT_EXCEEDED.
   */
  episodeResultBase.extend({ status: z.literal('ended_no_decision'), summary: z.string() }),
  episodeResultBase.extend({ status: z.literal('failed'), error: normalizedError }),
]);
export type EpisodeResult = z.infer<typeof episodeResult>;

/** Runtime health (§8.2 healthCheck). */
export const runtimeHealth = z.object({
  ok: z.boolean(),
  detail: z.string().optional(),
});
export type RuntimeHealth = z.infer<typeof runtimeHealth>;
